import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCode } from "antd";
import { MAIN_API } from "../../api/apilist";

function b64urlToBytes(str) {
  const pad = 4 - (str.length % 4);
  const s = str.replace(/-/g, "+").replace(/_/g, "/") + (pad === 4 ? "" : "=".repeat(pad));
  const bin = typeof atob === "function" ? atob(s) : Buffer.from(s, "base64").toString("binary");
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToB64url(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = typeof btoa === "function" ? btoa(bin) : Buffer.from(bin, "binary").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function hmacSha256B64url(keyRawBytes, messageStr) {
  if (!(window.crypto && window.crypto.subtle)) throw new Error("WebCrypto not available");
  const key = await window.crypto.subtle.importKey(
    "raw",
    keyRawBytes,
    { name: "HMAC", hash: { name: "SHA-256" } },
    false,
    ["sign"]
  );
  const enc = new TextEncoder();
  const sig = await window.crypto.subtle.sign("HMAC", key, enc.encode(messageStr));
  return bytesToB64url(new Uint8Array(sig));
}

const RollingQr = ({ ticketId, accessToken, width = 160, height = 160, className = "", style = {}, debug = false }) => {
  const [seed, setSeed] = useState(null); // { envelope, clientKey(bytes), stepSeconds, expiresAt }
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const refreshRef = useRef(null);

  const fetchSession = async () => {
    try {
      setError(null);
      const res = await fetch(`${MAIN_API}/api/ticket/qr/ticket/${ticketId}.session`, {
        cache: "no-store",
        mode: "cors",
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (res.status === 409) {
        throw new Error("outside dynamic window");
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const now = Number(data?.now || Math.floor(Date.now() / 1000));
      const stepSeconds = Number(data?.stepSeconds || 60);
      const ttl = Number(data?.sessionTtlSeconds || 600);
      const expiresAt = now + ttl;
      const clientKeyBytes = b64urlToBytes(String(data?.clientKey || ""));
      const envelope = String(data?.envelope || "");
      setSeed({ envelope, clientKeyBytes, stepSeconds, expiresAt });
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  useEffect(() => {
    if (!ticketId) return;
    fetchSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (refreshRef.current) clearTimeout(refreshRef.current);
    };
  }, [ticketId, accessToken]);

  useEffect(() => {
    if (!seed) return;
    // schedule refresh when session expires
    const nowS = Math.floor(Date.now() / 1000);
    const msLeft = Math.max(seed.expiresAt - nowS, 1) * 1000;
    refreshRef.current = setTimeout(() => {
      setSeed(null);
      setToken("");
      fetchSession();
    }, msLeft);

    const update = async () => {
      try {
        const nowSec = Math.floor(Date.now() / 1000);
        const m = Math.floor(nowSec / seed.stepSeconds);
        const signInput = `${seed.envelope}.${m}`;
        const sig = await hmacSha256B64url(seed.clientKeyBytes, signInput);
        setToken(`v3.${seed.envelope}.${m}.${sig}`);
      } catch (e) {
        setError(e?.message || String(e));
      }
    };
    update();
    timerRef.current = setInterval(update, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [seed]);

  if (error) {
    // If outside dynamic window or crypto unsupported, render nothing; parent can fallback
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eee', borderRadius: 8 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>QR no disponible</span>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eee', borderRadius: 8 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Generando QR...</span>
      </div>
    );
  }

  const size = Math.min(width, height);
  return (
    <div className={className} style={{ ...style, width, height, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <QRCode value={token} size={size} color="#000000" bgColor="#ffffff" />
      {debug ? (
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, textAlign: 'center', width: '100%' }}>
          v3 rolling
        </div>
      ) : null}
    </div>
  );
};

export default RollingQr;

